import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { userApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import hiveService from '../services/hive';
import './FollowList.css';

const FollowList = ({ onOpenLeftSidebar, onOpenRightSidebar, embedded }) => {
  const { username, listType } = useParams(); // listType = 'following' | 'followers'
  const location = useLocation();
  const isFollowingList = listType === 'following' || location.pathname.endsWith('/following');
  const { user: currentUser, isAuthenticated } = useAuth();

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [confirmTarget, setConfirmTarget] = useState(null); // name to unfollow
  const [unfollowing, setUnfollowing] = useState(null);    // name currently being processed

  // Search states
  const [localSearch, setLocalSearch] = useState('');
  
  const [blockchainSearch, setBlockchainSearch] = useState('');
  const [blockchainSearching, setBlockchainSearching] = useState(false);
  const [blockchainResults, setBlockchainResults] = useState([]);
  const [addingFromSearch, setAddingFromSearch] = useState({});

  // Lock / unlock body scroll when modal is open
  useEffect(() => {
    if (confirmTarget) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [confirmTarget]);

  // Fetch initial list
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAccounts([]);
    setLocalSearch('');
    setBlockchainSearch('');
    setBlockchainResults([]);

    const fetchPromise = isFollowingList
      ? userApi.getFollowing(username, 1000)
      : userApi.getFollowers(username, 1000);

    fetchPromise
      .then(data => {
        if (cancelled) return;
        const list = isFollowingList ? (data.following || []) : (data.followers || []);
        const names = list.map(entry => isFollowingList ? entry.following : entry.follower);
        setAccounts(names);
      })
      .catch(err => {
        if (!cancelled) setError('Failed to load list');
        console.error(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [username, listType, isFollowingList]);

  // Handle blockchain search
  useEffect(() => {
    const q = blockchainSearch.trim().toLowerCase();
    if (q.length < 3) {
      setBlockchainResults([]);
      setBlockchainSearching(false);
      return;
    }

    let cancelled = false;
    setBlockchainSearching(true);

    const timeoutId = setTimeout(async () => {
      try {
        const matches = await hiveService.searchAccounts(q, 10);
        if (cancelled) return;
        const currentSet = new Set(accounts.map(a => String(a).toLowerCase()));
        setBlockchainResults(matches.filter((name) => !currentSet.has(String(name).toLowerCase()) && String(name).toLowerCase() !== currentUser?.username));
      } catch (err) {
        if (cancelled) return;
        setBlockchainResults([]);
      } finally {
        if (!cancelled) setBlockchainSearching(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [blockchainSearch, accounts, currentUser]);

  const handleUnfollowConfirmed = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    setUnfollowing(target);
    try {
      await hiveService.unfollow(currentUser.username, target);
      setAccounts(prev => prev.filter(n => n !== target));
    } catch (err) {
      console.error('Unfollow error:', err);
      alert('Failed to unfollow @' + target);
    } finally {
      setUnfollowing(null);
    }
  };

  const handleFollowBlockchain = async (target) => {
    setAddingFromSearch(prev => ({...prev, [target]: true}));
    try {
      await hiveService.follow(currentUser.username, target);
      setAccounts(prev => [target, ...prev]);
      setBlockchainSearch('');
      setBlockchainResults([]);
    } catch (err) {
      console.error('Follow error:', err);
      alert('Failed to follow @' + target);
    } finally {
      setAddingFromSearch(prev => ({...prev, [target]: false}));
    }
  };

  const isOwner = isAuthenticated && currentUser?.username === username;
  const title = isFollowingList ? 'Following' : 'Followers';

  const filteredAccounts = accounts.filter(acc => 
    acc.toLowerCase().includes(localSearch.toLowerCase())
  );

  return (
    <>
      {!embedded && (
        <SidebarHeader
          title={`@${username} · ${title}`}
          onOpenLeftSidebar={onOpenLeftSidebar}
          onOpenRightSidebar={onOpenRightSidebar}
        />
      )}
      <div className={`follow-list-page ${embedded ? 'embedded' : ''}`}>
        
        {isOwner && isFollowingList && (
          <div className="follow-blockchain-search">
            <h4 className="follow-search-title">Find people to follow</h4>
            <input
              type="text"
              className="follow-search-input"
              placeholder="Search Hive accounts (min 3 chars)..."
              value={blockchainSearch}
              onChange={e => setBlockchainSearch(e.target.value)}
            />
            {blockchainSearching && <div className="follow-search-status">Searching...</div>}
            {!blockchainSearching && blockchainSearch.trim().length >= 3 && blockchainResults.length === 0 && (
              <div className="follow-search-status">No matching accounts found</div>
            )}
            {!blockchainSearching && blockchainResults.length > 0 && (
              <ul className="follow-list blockchain-results-list">
                {blockchainResults.map(name => (
                  <li key={name} className="follow-list-item">
                    <Link to={`/user/${name}`} className="follow-list-link">
                      <img
                        className="follow-list-avatar"
                        src={`https://images.hive.blog/u/${name}/avatar/small`}
                        alt={name}
                        onError={e => { e.target.src = '/default-avatar.png'; }}
                      />
                      <span className="follow-list-name">@{name}</span>
                    </Link>
                    <button
                      className="follow-btn"
                      disabled={addingFromSearch[name]}
                      onClick={() => handleFollowBlockchain(name)}
                    >
                      {addingFromSearch[name] ? 'Following...' : 'Follow'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="follow-local-search-container">
          <input 
            type="text" 
            className="follow-search-input local-search"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
        </div>

        {loading && <LoadingSpinner />}
        {error && <p className="follow-list-error">{error}</p>}
        {!loading && !error && accounts.length === 0 && (
          <p className="follow-list-empty">No accounts found.</p>
        )}
        {!loading && accounts.length > 0 && filteredAccounts.length === 0 && (
          <p className="follow-list-empty">No accounts match your search.</p>
        )}
        {!loading && filteredAccounts.length > 0 && (
          <ul className="follow-list">
            {filteredAccounts.map(name => (
              <li key={name} className="follow-list-item">
                <Link to={`/user/${name}`} className="follow-list-link">
                  <img
                    className="follow-list-avatar"
                    src={`https://images.hive.blog/u/${name}/avatar/small`}
                    alt={name}
                    onError={e => { e.target.src = '/default-avatar.png'; }}
                  />
                  <span className="follow-list-name">@{name}</span>
                </Link>
                {isOwner && isFollowingList && (
                  <button
                    className="unfollow-btn"
                    disabled={unfollowing === name}
                    onClick={() => setConfirmTarget(name)}
                  >
                    {unfollowing === name ? '...' : 'Unfollow'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmTarget && (
        <div className="unfollow-modal-overlay" onClick={() => setConfirmTarget(null)}>
          <div className="unfollow-modal" onClick={e => e.stopPropagation()}>
            <p className="unfollow-modal-text">Unfollow <strong>@{confirmTarget}</strong>?</p>
            <div className="unfollow-modal-actions">
              <button className="unfollow-modal-cancel" onClick={() => setConfirmTarget(null)}>Cancel</button>
              <button className="unfollow-modal-confirm" onClick={handleUnfollowConfirmed}>Unfollow</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FollowList;
